// Auth API — uses plain fetch to avoid the Bearer interceptor in client.ts.
// These endpoints don't require (or should not retry with) an access token.

import { env } from '@/config/env';
import { ApiError } from './client';
import type { UserProfile } from '@/types';

export interface AuthSession {
  accessToken: string;
  user: UserProfile;
}

async function authPost<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${env.apiUrl}${path}`, {
    method: 'POST',
    credentials: 'include', // send httpOnly cookie
    headers: { 'Content-Type': 'application/json' },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({})) as {
      error?: { code?: string; message?: string };
    };
    throw new ApiError(
      res.status,
      data.error?.code ?? 'UNKNOWN_ERROR',
      data.error?.message ?? res.statusText,
    );
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export function exchangeCode(code: string): Promise<AuthSession> {
  return authPost<AuthSession>('/auth/exchange', { code });
}

export function refreshSession(): Promise<AuthSession> {
  return authPost<AuthSession>('/auth/refresh');
}

export function logoutRequest(accessToken: string): Promise<void> {
  return fetch(`${env.apiUrl}/auth/logout`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
  }).then(() => undefined);
}
